import { useEffect, useState } from 'react';
import { Microphone } from '@phosphor-icons/react';
import BottomSheet from '../shared/BottomSheet';
import { useVoiceSearch } from '../../hooks/useVoiceSearch';

const SUGGESTIONS = ['pizza', 'tacos', 'birria', 'hamburguesa', 'sushi', 'ramen', 'pozole'];
const ERROR_CHIPS = ['Pizza', 'Tacos', 'Hamburguesa'];

const pickSuggestion = () => SUGGESTIONS[Math.floor(Math.random() * SUGGESTIONS.length)];

// Qué decir según por qué falló.
const MENSAJES_ERROR = {
  'no-speech': 'Lo siento, no te entendí. Intenta diciendo...',
  'not-allowed': 'Para buscar por voz, permite el micrófono en los ajustes del navegador.',
  network: 'No se pudo conectar con el reconocimiento de voz. Revisa tu internet.',
  otro: 'La búsqueda por voz no está disponible ahora.',
};

/**
 * Bottom sheet de búsqueda por voz. Dos estados visuales:
 *   - escuchando — "Hola, estoy escuchando. Di..." con el micrófono animado;
 *                  en cuanto se entiende algo, se muestra en vivo (como el
 *                  panel de voz de Google)
 *   - error      — dice por qué falló; reintentar, o dictar con el teclado
 *                  (el micrófono del teclado del celular)
 *
 * Al recibir un transcript válido, llama `onTranscript(text)` y cierra.
 * `onUsarTeclado` (opcional): cierra y deja el buscador listo para escribir.
 */
export default function VoiceSearchSheet({ isOpen, onClose, onTranscript, onUsarTeclado, zIndex }) {
  const { state, transcript, parcial, error, start, stop, reset } = useVoiceSearch({ lang: 'es-MX' });
  const [suggestion, setSuggestion] = useState(pickSuggestion);

  // Cada vez que se abre: arranca a escuchar de inmediato. Así va dentro del
  // mismo toque que abrió la hoja (algunos navegadores solo dejan usar el
  // micrófono en respuesta a un toque); antes esperaba 800 ms.
  useEffect(() => {
    if (!isOpen) {
      stop();
      return;
    }
    setSuggestion(pickSuggestion());
    reset();
    start();
    return () => stop();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Cuando llega un transcript válido, propagar y cerrar.
  useEffect(() => {
    if (transcript && isOpen) {
      onTranscript?.(transcript);
      onClose?.();
    }
  }, [transcript, isOpen, onTranscript, onClose]);

  const handleRetry = () => {
    reset();
    start();
  };

  const usarTeclado = () => {
    onClose?.();
    onUsarTeclado?.();
  };

  const conError = state === 'error';
  const sinPermiso = error === 'not-allowed';

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} showCloseButton zIndex={zIndex}>
      <div className="px-6 pt-8 pb-10 text-center min-h-[260px] flex flex-col items-center justify-center">
        {!conError && (
          <>
            {parcial ? (
              <p className="text-2xl font-extrabold text-gray-900 mb-8 line-clamp-2">{parcial}</p>
            ) : (
              <>
                <p className="text-base text-gray-500 mb-1.5">Hola, estoy escuchando. Di...</p>
                <p className="text-2xl font-extrabold text-gray-900 mb-8">"{suggestion}"</p>
              </>
            )}
            <div className="relative w-20 h-20 flex items-center justify-center">
              {state === 'listening' && (
                <>
                  <div className="absolute inset-0 bg-cta-bg/30 rounded-full animate-ping" />
                  <div className="absolute inset-2 bg-cta-bg/40 rounded-full animate-pulse" />
                </>
              )}
              <div className="relative w-16 h-16 bg-cta-bg rounded-full flex items-center justify-center shadow-md">
                <Microphone size={28} weight="fill" className="text-white" />
              </div>
            </div>
          </>
        )}

        {conError && (
          <>
            <p className="text-base text-gray-500 mb-4">{MENSAJES_ERROR[error] || MENSAJES_ERROR.otro}</p>
            {error === 'no-speech' && (
              <div className="flex flex-wrap justify-center gap-2 mb-7">
                {ERROR_CHIPS.map(s => (
                  <span
                    key={s}
                    className="px-4 py-1.5 border border-gray-300 rounded-full text-sm text-gray-700"
                  >
                    {s}
                  </span>
                ))}
              </div>
            )}
            {!sinPermiso && (
              <>
                <button
                  type="button"
                  onClick={handleRetry}
                  className="w-14 h-14 bg-cta-bg rounded-full flex items-center justify-center shadow-md active:scale-95 transition-transform"
                  aria-label="Intentar de nuevo"
                >
                  <Microphone size={24} weight="fill" className="text-white" />
                </button>
                <button
                  type="button"
                  onClick={handleRetry}
                  className="mt-3 text-sm font-semibold text-cta-bg"
                >
                  Toca el micrófono para intentar de nuevo
                </button>
              </>
            )}
            {onUsarTeclado && (
              <button
                type="button"
                onClick={usarTeclado}
                className="mt-4 text-sm font-medium text-gray-500 underline"
              >
                Escribir o dictar con el teclado
              </button>
            )}
          </>
        )}
      </div>
    </BottomSheet>
  );
}
